import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Home from "./Pages/Home/Home";
import NotFound from "./Pages/NotFound/NotFound";

import Cart from "./Pages/Cart/Cart";
import Login from "./Pages/Auth/Login";
import SignUp from "./Pages/Auth/SignUp";
import ScrollToTop from "./ScrollToTop";
import OrderDetails from "./Pages/OrderDetails/OrderDetails";
import Shop from "./Pages/Shop/Shop";
import ShopCategory from "./Pages/Shop/ShopCategory";
import ProductDetails from "./Pages/Product/ProductDetails";
import AllProducts from "./Pages/Product/AllProducts";
import SellerStorefront from "./Pages/Product/SellerStorefront";
import MobileFooter from "./Components/Mobile/MobileFooter";
import MobileWindow from "./Components/Mobile/MobileWindow";
import AccountPage from "./Pages/Auth/AccountPage";
import ResetPassword from "./Pages/Auth/ResetPassword";
import Checkout from "./Pages/Cart/Checkout";
import ThankYou from "./Pages/Cart/ThankYou";
import OrderList from "./Pages/OrderDetails/OrderList";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { account, tablesDB, DB_ID, USERS_TABLE } from "./appwrite";
import { useStateValue } from "./StateProvider";
import BackToTop from "./BackToTop";
import useLogVisitor from "./LogVisitor";
import VerifyEmail from "./Pages/Auth/VerifyEmail";
import LoadingScreen from "./LoadingScreen";
import "./App.css";
import ColorCode from "./ColorCode";
import ConditionalLiveChat from "./ConditionalLiveChat";
import SignupRouter from "./Pages/Auth/SignupRouter";
import FrequentlyAsked from "./Pages/Home/FrequentlyAsked";
import SellerReupVaultPage from "./Pages/Shop/SellerReupVaultPage";
import RefundPolicy from "./Pages/Auth/RefundPolicy";
import TermsCondition from "./Pages/Auth/TermsCondition";
import PrivacyPolicy from "./Pages/Auth/PrivacyPolicy";
import EmailVerified from "./Pages/Auth/EmailVerified";
import PasswordReset from "./Pages/Auth/PasswordReset";
import ResetConfirmation from "./Pages/Auth/ResetConfirmation";
import ChangePassword from "./Pages/Auth/ChangePassword";
import OneTime from "./Pages/Auth/OneTime";

// function ScrollManager() {
//   const location = useLocation();
//   return <ScrollToTop hidden={location.pathname === "/live-chat"} />;
// }

export default function App() {
  const [, dispatch] = useStateValue();
  const [loading, setLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);

  useLogVisitor();

  useEffect(() => {
    const minDisplayTime = 1000;
    const startTime = Date.now();

    const fetchData = async () => {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setDataFetched(true);

      const elapsedTime = Date.now() - startTime;
      const remainingTime = minDisplayTime - elapsedTime;
      if (remainingTime > 0) {
        setTimeout(() => setLoading(false), remainingTime);
      } else {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    account.get()
      .then(async (user) => {
        try {
          const doc = await tablesDB.getRow({
            databaseId: DB_ID,
            tableId: USERS_TABLE,
            rowId: user.$id,
          });
          dispatch({ type: "SET_USER", user: { ...user, ...doc } });
        } catch {
          dispatch({ type: "SET_USER", user });
        }
      })
      .catch(() => dispatch({ type: "SET_USER", user: null }));
  }, [dispatch]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <ScrollToTop />
      <ColorCode />
      <div className="app">
        <ToastContainer />
        <Routes>
          <Route
            path="/frequently-asked-questions"
            element={<FrequentlyAsked />}
          />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsCondition />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/live-chat" element={<MobileWindow />} />
          <Route path="/account/reset-password" element={<ResetPassword />} />
          <Route path="/account/change-password" element={<ChangePassword />} />
          <Route path="/account/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/verify/otp" element={<OneTime />} />
          <Route
            path="/account/email-verification"
            element={<EmailVerified />}
          />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/signup" element={<SignupRouter />} />
          <Route path="/login" element={<Login />} />
          <Route path="/shop/all" element={<AllProducts />} />
          <Route path="/seller/:sellerId" element={<SellerStorefront />} />
          <Route path="/product/:productName" element={<ProductDetails />} />
          <Route
            path="/shop/reup-vault/seller/:seller_name/:encoded_seller_id"
            element={<SellerReupVaultPage />}
          />

          <Route
            path="/shop/reup-vault"
            element={<ShopCategory category="ReUp Vault" />}
          />
          <Route
            path="/shop/value-packs"
            element={<ShopCategory category="Value Packs" />}
          />

          <Route
            path="/shop/fresh-farm-produce"
            element={<ShopCategory category="Fresh Farm Produce" />}
          />
          <Route
            path="/shop/grains-and-staples"
            element={<ShopCategory category="Grains & Staples" />}
          />
          <Route
            path="/shop/meat-and-livestock"
            element={<ShopCategory category="Meat & Livestock" />}
          />
          <Route
            path="/shop/catfish"
            element={<ShopCategory category="Catfish" />}
          />
          <Route
            path="/shop/dairy-and-eggs"
            element={<ShopCategory category="Dairy & Eggs" />}
          />
          <Route
            path="/shop/beverages"
            element={<ShopCategory category="Beverages" />}
          />
          <Route
            path="/shop/snacks-and-confectionery"
            element={<ShopCategory category="Snacks & Confectionery" />}
          />
          <Route
            path="/shop/canned-and-packaged-foods"
            element={<ShopCategory category="Canned & Packaged Foods" />}
          />
          <Route
            path="/shop/frozen-foods"
            element={<ShopCategory category="Frozen Foods" />}
          />
          <Route
            path="/shop/condiments-and-sauces"
            element={<ShopCategory category="Condiments & Sauces" />}
          />
          <Route path="/shop" element={<Shop />} />
          <Route path="/order/:orderId" element={<OrderDetails />} />
          <Route path="/orders" element={<OrderList />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="*" element={<NotFound />} />
          <Route path="/" element={<Home />} />
        </Routes>
        <MobileFooter />
      </div>
      <ConditionalLiveChat />
      {/* <BackToTop /> */}
    </Router>
  );
}
